/**
 * Compact markdown renderer for activity ticker, History tab, etc.
 *
 * Uses react-markdown with styling suited for small UI areas.
 * Supports tables, lists, code blocks, and all standard markdown.
 */

import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import type { MouseEvent, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { AttachmentImage, AttachmentLink } from "./attachment-media";
import { uploadAssetPath } from "@/lib/attachment-media";

export type MarkdownLinkClickHandler = (
  href: string,
  text: string,
  event: MouseEvent<HTMLAnchorElement>,
) => boolean | void;

function childrenToText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(childrenToText).join("");
  if (children && typeof children === "object" && "props" in children) {
    return childrenToText(
      (children as { props: { children?: ReactNode } }).props.children,
    );
  }
  return "";
}

function createCompactComponents(
  onLinkClick?: MarkdownLinkClickHandler,
): Components {
  return {
    h1: ({ children }) => (
      <p className="font-semibold text-primary">{children}</p>
    ),
    h2: ({ children }) => (
      <p className="font-semibold text-primary">{children}</p>
    ),
    h3: ({ children }) => (
      <p className="font-medium text-primary">{children}</p>
    ),
    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
    strong: ({ children }) => (
      <strong className="font-semibold text-primary">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    ul: ({ children }) => (
      <ul className="mb-1 list-disc pl-3.5 last:mb-0">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="mb-1 list-decimal pl-3.5 last:mb-0">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-snug">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-glass-stroke pl-2 text-muted italic">
        {children}
      </blockquote>
    ),
    code: ({ children }) => (
      <code className="rounded-control-sm bg-glass-fill-heavy px-appkit-2xs py-px font-mono text-[0.9em]">
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre className="scrollbar-none my-1 overflow-x-auto rounded-control-sm bg-glass-fill p-appkit-2xs font-mono text-[0.9em]">
        {children}
      </pre>
    ),
    img: ({ src, alt }) => <AttachmentImage src={src} alt={alt} />,
    a: ({ href, children }) => (
      <AttachmentLink
        href={href}
        className="nodrag nopan text-accent underline underline-offset-2"
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.stopPropagation();
          const handled = onLinkClick?.(
            uploadAssetPath(href ?? "")
              ? event.currentTarget.href
              : (href ?? ""),
            childrenToText(children),
            event,
          );
          if (handled) {
            event.preventDefault();
          }
        }}
        data-testid="markdown-content-link"
      >
        {children}
      </AttachmentLink>
    ),
    table: ({ children }) => (
      <div className="scrollbar-none my-1 overflow-x-auto">
        <table className="w-full border-collapse text-[inherit]">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="border-b border-glass-stroke">{children}</thead>
    ),
    tbody: ({ children }) => <tbody>{children}</tbody>,
    tr: ({ children }) => (
      <tr className="border-b border-glass-stroke-faint last:border-0">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="px-appkit-2xs py-appkit-2xs text-left font-semibold text-primary">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="px-appkit-2xs py-appkit-2xs text-secondary">{children}</td>
    ),
    hr: () => <hr className="my-1 border-glass-stroke-faint" />,
  };
}

function transformUrl(value: string, allowDenkerProtocol: boolean): string {
  return allowDenkerProtocol && value.startsWith("denker://")
    ? value
    : defaultUrlTransform(value);
}

interface MarkdownContentProps {
  content: string;
  className?: string;
  onLinkClick?: MarkdownLinkClickHandler;
}

export function MarkdownContent({
  content,
  className,
  onLinkClick,
}: MarkdownContentProps) {
  return (
    <div
      className={cn(
        // `min-w-0` lets us shrink inside flex parents; `break-words` wraps
        // long URLs and identifiers so they don't push the panel sideways.
        // Tables and code blocks still scroll horizontally inside themselves.
        "min-w-0 break-words text-[inherit] leading-snug",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={createCompactComponents(onLinkClick)}
        urlTransform={(value) => transformUrl(value, Boolean(onLinkClick))}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
